import { createSelector, createStructuredSelector } from 'reselect';
import type Highcharts from 'highcharts';
import type { SeriesHeatmapOptions } from 'highcharts';
import type { Range } from '@/scatterPlot/scatterPlot.constants';
import { DENSITY_PLOT_ID } from '@/scatterPlot/scatterPlot.constants';
import type { ScatterPlotStoreState } from '@/scatterPlot/scatterPlot.store';
import type { ItemWithUnits } from '@/utilities/unit.utilities';
import { getParenthesizedUnitString, resolveUnitOfMeasure } from '@/utilities/unit.utilities';

type DensityPlotDatum = [number, number, number | null | undefined];

interface DensityPlotDataResult {
  densityPlotData: DensityPlotDatum[];
}

type DensityPlotDisplayState = Pick<ScatterPlotStoreState, 'xBinSize' | 'yBinSize' | 'showTooltips'>;

interface ColorAxisRangeWithMid extends Range {
  mid: number;
}

type TrendSignalSource = Partial<ItemWithUnits> & {
  id?: string;
  name?: string;
  color?: string;
  format?: string;
  formatOptions?: { format?: string };
};

interface TrendSignalSelection extends ItemWithUnits {
  id: string;
  name: string;
  color: string;
  format?: string;
}

interface DensityPlotChartParams {
  state: ScatterPlotStoreState;
  densityPlotData: DensityPlotDatum[];
  xSignal?: TrendSignalSource;
  ySignal?: TrendSignalSource;
  colorAxisRange: Range;
}

// White out the cells with no significant data
export const TIME_COLOR_STOPS_WITH_ZERO: [number, string][] = [
  [0, '#ffffff'],
  [0.00001, '#3a8af2'],
  [0.3, '#fff700'],
  [0.7, '#e87217'],
  [1, '#f51c07'],
];

// Sometimes, all cells will have some data, so we don't need to white out
// the cells with the shortest duration
const TIME_COLOR_STOPS_WITHOUT_ZERO: [number, string][] = [
  [0, '#3a8af2'],
  [0.3, '#fff700'],
  [0.7, '#e87217'],
  [1, '#f51c07'],
];

// Make the bin size for display slightly larger to avoid empty pixels in the plot. This occasionally has the side
// effect of density plot cells changing size when the user mouses over them, but makes the plot look much better in
// organizer screenshots.
export const BIN_SIZE_MULTIPLIER = 1.001;

const makeTrendSignalSelector = () =>
  createStructuredSelector<TrendSignalSource | undefined, TrendSignalSelection>({
    id: (item) => item?.id ?? '',
    name: (item) => item?.name ?? '',
    color: (item) => item?.color ?? 'transparent',
    valueUnitOfMeasure: (item) => {
      if (item) {
        return resolveUnitOfMeasure(item as ItemWithUnits).value;
      } else {
        return '';
      }
    },
    format: (item) => item?.formatOptions?.format,
  });

const densityPlotSelector = createStructuredSelector<ScatterPlotStoreState, DensityPlotDisplayState>({
  xBinSize: (state) => state.xBinSize,
  yBinSize: (state) => state.yBinSize,
  showTooltips: (state) => state.showTooltips,
});

const densityPlotDataSelector = createStructuredSelector<DensityPlotDatum[], DensityPlotDataResult>({
  densityPlotData: (densityPlotData) => densityPlotData,
});

const colorAxisRangeSelector = createStructuredSelector<Range, ColorAxisRangeWithMid>({
  start: (colorAxisRange) => colorAxisRange.start,
  end: (colorAxisRange) => colorAxisRange.end,
  mid: (colorAxisRange) => (colorAxisRange.start + colorAxisRange.end) / 2,
});
const getNameForAxis = (item: TrendSignalSelection) => [item.name, getParenthesizedUnitString(item)].join('');
const getColorForAxis = (item: TrendSignalSelection) => (item.color ? item.color : 'transparent');

export interface SeeqDensityPlotPoint extends Highcharts.Point {
  value: DensityPlotDatum[2];
  series: Highcharts.Series & { userOptions: SeriesHeatmapOptions };
}

/**
 * A selector that takes in the heat map state and the X and Y signals and produces Highcharts config object
 * that it can use to re-render.
 *
 * @param {Object} densityPlotState - The state from the sqScatterPlotStore
 * @param {Object} xSignalState - The state from trendSignalSelector(xSignal)
 * @param {Object} ySignalState - The state from trendSignalSelector(ySignal)
 * @return {Function => Object} Highcharts config for the axes, the scatter plot series, and regression lines.
 */
export const getDensityPlotChartConfig = createSelector(
  createSelector(({ state }: DensityPlotChartParams) => state, densityPlotSelector),
  createSelector(({ densityPlotData }: DensityPlotChartParams) => densityPlotData, densityPlotDataSelector),
  createSelector(({ xSignal }: DensityPlotChartParams) => xSignal, makeTrendSignalSelector()),
  createSelector(({ ySignal }: DensityPlotChartParams) => ySignal, makeTrendSignalSelector()),
  createSelector(({ colorAxisRange }: DensityPlotChartParams) => colorAxisRange, colorAxisRangeSelector),
  (
    densityPlot: DensityPlotDisplayState,
    densityPlotData: DensityPlotDataResult,
    xSignal: TrendSignalSelection,
    ySignal: TrendSignalSelection,
    colorAxisRange: ColorAxisRangeWithMid,
  ): Highcharts.Options => ({
    xAxis: {
      id: xSignal.id,
      signalId: xSignal.id,
      title: {
        text: getNameForAxis(xSignal),
        style: { color: getColorForAxis(xSignal) },
      },
    },
    yAxis: {
      id: ySignal.id,
      signalId: ySignal.id,
      title: {
        text: getNameForAxis(ySignal),
        style: { color: getColorForAxis(ySignal) },
      },
    },
    tooltip: {
      enabled: densityPlot.showTooltips,
    },
    colorAxis: {
      stops: colorAxisRange.start > 0 ? TIME_COLOR_STOPS_WITHOUT_ZERO : TIME_COLOR_STOPS_WITH_ZERO,
      tickPositions: [colorAxisRange.start, colorAxisRange.mid, colorAxisRange.end],
    },
    series: [
      {
        type: 'heatmap',
        id: DENSITY_PLOT_ID,
        borderWidth: 0,
        data: densityPlotData.densityPlotData,
        xNumberFormat: xSignal.format,
        yNumberFormat: ySignal.format,
        rowsize: densityPlot.yBinSize * BIN_SIZE_MULTIPLIER,
        colsize: densityPlot.xBinSize * BIN_SIZE_MULTIPLIER,
        xSignalName: xSignal.name,
        ySignalName: ySignal.name,
      },
    ],
  }),
);
